// src/store/hooks.ts

import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from './index'
import { useMemo, useEffect, useState } from 'react'
import { useGetAnnotationStatisticsQuery } from '../services/annotationApi'
import { useGetTagsQuery } from '../services/tagApi'

export const FALLBACK_COLORS = [
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
]

export interface ClassDistributionItem {
  id: string
  name: string
  value: number
  color: string
  percentage: number
  balance: string
}

export const useClassDistribution = (
  projectId: string,
  datasetId: string,
  datasources?: string[]
) => {
  const { data: annoStats } = useAnnotationStats(projectId, datasetId, datasources)

  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: projectId,
      dataset_id: datasetId,
    },
    {
      skip: !projectId || !datasetId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  const tags = useMemo(() => tagsData?.tags || [], [tagsData?.tags])

  const tagIdToInfoMap = useMemo(() => {
    return tags.reduce((acc, tag) => {
      acc[tag.id] = {
        name: tag.name,
        color: `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`,
      }
      return acc
    }, {} as Record<string, { name: string; color: string }>)
  }, [tags])

  const classDistribution = useMemo(() => {
    if (!annoStats?.statistics?.annotations_per_class) return []

    // Get the distribution data from annotations_per_class
    const distribution = Object.entries(annoStats.statistics.annotations_per_class)
      .map(([tagId, classData]) => {
        const tagInfo = tagIdToInfoMap[tagId]
        const fallbackColor =
          FALLBACK_COLORS[
            Math.abs(tagId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) %
              FALLBACK_COLORS.length
          ]

        return {
          id: tagId,
          name: tagInfo?.name || classData.name || 'Unknown',
          value: classData.count,
          color: tagInfo?.color || fallbackColor,
          balance: classData.balance || 'undetermined',
          percentage: 0, // Will be calculated below
        }
      })
      .filter((val) => val.id !== 'null' && val.name !== 'Unknown')
      .sort((a, b) => b.value - a.value)

    if (distribution.length === 0) return []

    const totalCount = distribution.reduce((sum, item) => sum + item.value, 0)

    return distribution.map((item) => ({
      ...item,
      percentage: totalCount > 0 ? (item.value / totalCount) * 100 : 0,
    }))
  }, [annoStats?.statistics?.annotations_per_class, tagIdToInfoMap])

  return classDistribution
}

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export function useObjectUrl(blob: Blob | undefined) {
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!blob) {
      return
    }

    const url = URL.createObjectURL(blob)
    setObjectUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [blob])

  return objectUrl
}

export const useSelectTagsByDataset = (projectId: string, datasetId: string) => {
  const { data } = useGetTagsQuery(
    {
      project_id: projectId,
      dataset_id: datasetId,
    },
    {
      skip: !projectId || !datasetId,
    }
  )
  return data?.tags || []
}

export const useAnnotationStats = (
  projectId: string,
  datasetId: string,
  datasources?: string[],
  options?: { skip?: boolean }
) => {
  return useGetAnnotationStatisticsQuery(
    {
      projectId: projectId || '',
      datasetId: datasetId || '',
      metadata_filter:
        datasources && datasources.length > 0 ? { datasource: datasources } : undefined,
    },
    {
      skip: options?.skip || !projectId || !datasetId,
    }
  )
}
